import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { CustomSpinner } from '../../../components/CustomSpinner'
import HistoricoSaldoService from '../../../services/database/HistoricoSaldoService'
import { toast } from 'react-toastify'
import { formatPrice } from '../../../utils/formatPrice'
import noBalanceImg from '../../../assets/illustration/no-balance.svg'

import './styles.css'

export const ModalHistoricoSaldo = forwardRef(
  ({ isOpen, onClose, pessoa }, ref) => {
    const [historico, setHistorico] = useState([])
    const [isLoadingHistorico, setIsLoadingHistorico] = useState(false)

    const loadHistorico = useCallback(async () => {
      setIsLoadingHistorico(true)
      try {
        const data = await HistoricoSaldoService.getByIDCliente(pessoa.cod_pessoa)
        setHistorico(data)
      } catch (err) {
        console.error(err)
        toast.error('Erro ao carregar o histórico, tente novamente mais tarde!')
      } finally {
        setIsLoadingHistorico(false)
      }
    }, [pessoa.cod_pessoa])

    useEffect(() => { loadHistorico() }, [loadHistorico])

    // Totalizadores
    const totalEntradas = historico
      .filter((d) => d.tipo?.trim() === 'entrada')
      .reduce((s, d) => s + (d.valor ?? 0), 0)

    const totalSaidas = historico
      .filter((d) => d.tipo?.trim() === 'saida')
      .reduce((s, d) => s + (d.valor ?? 0), 0)

    const saldoAtualFinal = historico.length > 0
      ? (historico[historico.length - 1]?.saldo_atual ?? 0)
      : 0

    return (
      <Modal
        ref={ref}
        show={isOpen}
        onHide={onClose}
        centered
        size="xl"
        dialogClassName="modal-historico-saldo-dialog"
      >
        {/* Header */}
        <Modal.Header
          closeButton
          style={{ borderBottom: '1px solid #e8e8e8', padding: '1rem 1.25rem' }}
        >
          <div>
            <Modal.Title style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
              Histórico de saldo
            </Modal.Title>
            <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
              Cliente:{' '}
              <strong style={{ color: '#185FA5', fontWeight: 600 }}>
                {pessoa?.pes_razao ?? `#${pessoa.cod_pessoa}`}
              </strong>
            </p>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: '1.25rem' }}>
          {isLoadingHistorico ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2.5rem 0' }}>
              <CustomSpinner />
            </div>
          ) : historico.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 0', gap: 12 }}>
              <img src={noBalanceImg} style={{ width: 180, opacity: 0.7 }} alt="Sem histórico" />
              <p style={{ fontSize: 14, color: '#aaa' }}>Nenhum registro de histórico encontrado.</p>
            </div>
          ) : (
            <>
              {/* Cards de resumo */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
                {[
                  { label: 'Registros',        value: historico.length,          color: '#185FA5' },
                  { label: 'Total entradas',   value: formatPrice(totalEntradas), color: '#3B6D11' },
                  { label: 'Total saídas',     value: formatPrice(totalSaidas),   color: '#A32D2D' },
                  { label: 'Saldo atual',      value: formatPrice(saldoAtualFinal), color: saldoAtualFinal >= 0 ? '#3B6D11' : '#A32D2D' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: '#f7f7f8', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: 17, fontWeight: 600, color }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Tabela */}
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, tableLayout: 'fixed', minWidth: 740 }}>
                  <colgroup>
                    <col style={{ width: 55 }} />
                    <col style={{ width: 85 }} />
                    <col style={{ width: 115 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 105 }} />
                    <col style={{ width: 115 }} />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#f7f7f8', borderBottom: '2px solid #e8e8e8' }}>
                      {['ID', 'Pedido', 'Saldo Anterior', 'Tipo', 'Valor', 'Saldo Atual', 'Cliente', 'Funcionário'].map((label, i) => (
                        <th
                          key={label}
                          style={{
                            padding: '9px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#555',
                            whiteSpace: 'nowrap',
                            textAlign: i >= 2 && i <= 5 ? 'right' : 'left',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historico.map((data, idx) => {
                      const isSaida = data.tipo?.trim() === 'saida'
                      const saldoSubiu = (data.saldo_atual ?? 0) >= (data.saldo_anterior ?? 0)

                      return (
                        <tr
                          key={data.id}
                          style={{
                            background: idx % 2 === 0 ? '#fff' : '#fafafa',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff' }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa' }}
                        >
                          <td style={td()}><span style={{ color: '#aaa', fontSize: 11 }}>{data.id}</span></td>
                          <td style={td()}>
                            {data.id_pedido
                              ? <span>#{data.id_pedido}</span>
                              : <span style={{ color: '#ccc', fontSize: 11 }}>—</span>
                            }
                          </td>
                          <td style={td('right')}>
                            <span style={{ color: '#888' }}>{formatPrice(data.saldo_anterior ?? 0)}</span>
                          </td>
                          <td style={td('right')}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 10px',
                              borderRadius: 20,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              background: isSaida ? '#FCEBEB' : '#EAF3DE',
                              color: isSaida ? '#A32D2D' : '#3B6D11',
                            }}>
                              {isSaida ? '▼ SAÍDA' : '▲ ENTRADA'}
                            </span>
                          </td>
                          <td style={{ ...td('right'), fontWeight: 600, color: isSaida ? '#A32D2D' : '#3B6D11' }}>
                            {isSaida ? '− ' : '+ '}{formatPrice(data.valor ?? 0)}
                          </td>
                          <td style={{ ...td('right'), fontWeight: 600, color: saldoSubiu ? '#3B6D11' : '#A32D2D' }}>
                            {formatPrice(data.saldo_atual ?? 0)}
                          </td>
                          <td style={td()}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.nome_cliente}>
                              {data.nome_cliente}
                            </span>
                          </td>
                          <td style={td()}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.nome_funcionario}>
                              {data.nome_funcionario}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>

        {/* Footer */}
        {!isLoadingHistorico && historico.length > 0 && (
          <Modal.Footer style={{ borderTop: '1px solid #e8e8e8', padding: '0.75rem 1.25rem', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#888' }}>
              {historico.length} registro{historico.length !== 1 ? 's' : ''} no histórico
            </span>
            <div style={{ display: 'flex', gap: 16 }}>
              <span style={{ fontSize: 12, color: '#888' }}>
                Entradas: <strong style={{ color: '#3B6D11' }}>{formatPrice(totalEntradas)}</strong>
              </span>
              <span style={{ fontSize: 12, color: '#888' }}>
                Saídas: <strong style={{ color: '#A32D2D' }}>{formatPrice(totalSaidas)}</strong>
              </span>
              <span style={{ fontSize: 12, color: '#888' }}>
                Saldo atual: <strong style={{ color: saldoAtualFinal >= 0 ? '#3B6D11' : '#A32D2D' }}>{formatPrice(saldoAtualFinal)}</strong>
              </span>
            </div>
          </Modal.Footer>
        )}
      </Modal>
    )
  },
)

function td(align = 'left') {
  return {
    padding: '8px 10px',
    textAlign: align,
    fontSize: 12.5,
    color: '#222',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  }
}